/**
 * Output panel. The export-only "Output" card: file format
 * (CSV / Parquet / Excel), resample frequency, and missing-data fill. Each
 * control writes its atom; the request shape is assembled in `state/request.ts`
 * (`buildExportRequest`), so nothing here knows the wire format.
 *
 * Re-estimate / URL-sync wiring matches the blob exactly: format and frequency
 * change the row count or byte size, so they re-estimate; fill never changes the
 * estimate, so it only re-syncs the URL.
 */

import { html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import {
  exportFormat,
  fillMethod,
  frequencyValue,
  frequencyUnit,
} from '../../state/store.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import type { ExportFormat, FillMethod } from '../../api/types.ts';

const FORMATS: Array<{ value: ExportFormat; label: string }> = [
  { value: 'csv', label: 'CSV' },
  { value: 'parquet', label: 'Parquet' },
  { value: 'xlsx', label: 'Excel' },
];

@customElement('output-panel')
export class OutputPanel extends LightElement {
  @state() private open = false;

  private store = new StoreController(this, [
    exportFormat,
    fillMethod,
    frequencyValue,
    frequencyUnit,
  ]);

  private setFormat(fmt: ExportFormat): void {
    exportFormat.set(fmt);
    scheduleEstimate();
    syncToUrl();
  }

  private setFreqValue(v: string): void {
    frequencyValue.set(v);
    scheduleEstimate();
    syncToUrl();
  }

  private setFreqUnit(u: 'min' | 'h' | 'd'): void {
    frequencyUnit.set(u);
    scheduleEstimate();
    syncToUrl();
  }

  private setFill(m: FillMethod): void {
    // Fill never changes the estimate (same rows, same bytes) - URL only.
    fillMethod.set(m);
    syncToUrl();
  }

  override render(): TemplateResult {
    const fmt = exportFormat.value;
    return html`
      <div class="card eos-only" data-tab="export">
        <button
          type="button"
          class="section-header output-toggle"
          aria-expanded=${String(this.open)}
          aria-controls="output-fields"
          @click=${() => (this.open = !this.open)}
        >
          <span class="arrow ${this.open ? 'open' : ''}" aria-hidden="true">▶</span>
          <span>Output</span>
        </button>

        <div id="output-fields" class="section-body ${this.open ? '' : 'collapsed'}">

        <div class="output-field">
          <label>Format</label>
          <div class="radio-group">
            ${FORMATS.map(
              (f) => html`
                <div class="radio-option">
                  <input
                    type="radio"
                    name="format"
                    id="fmt-${f.value}"
                    value=${f.value}
                    .checked=${fmt === f.value}
                    @change=${() => this.setFormat(f.value)}
                  />
                  <label for="fmt-${f.value}">${f.label}</label>
                </div>
              `,
            )}
          </div>
        </div>

        <div class="output-field">
          <label>
            Resample frequency
            <span class="field-hint">(default: 3min)</span>
          </label>
          <div class="freq-row">
            <input
              type="number"
              min="1"
              placeholder="3"
              class="freq-value"
              .value=${frequencyValue.value}
              @input=${(e: Event) => this.setFreqValue((e.target as HTMLInputElement).value)}
            />
            <select
              .value=${frequencyUnit.value}
              @change=${(e: Event) =>
                this.setFreqUnit((e.target as HTMLSelectElement).value as 'min' | 'h' | 'd')}
            >
              <option value="min">Minutes</option>
              <option value="h">Hours</option>
              <option value="d">Days</option>
            </select>
          </div>
        </div>

        <div class="output-field">
          <label for="fill-method">Fill missing data</label>
          <select
            id="fill-method"
            .value=${fillMethod.value}
            @change=${(e: Event) =>
              this.setFill((e.target as HTMLSelectElement).value as FillMethod)}
          >
            <option value="none">None</option>
            <option value="forward">Forward fill</option>
            <option value="backward">Backward fill</option>
          </select>
        </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'output-panel': OutputPanel;
  }
}
